import { useEffect } from "react";
import { Link } from "react-router-dom";
import MultiModalInput from "./MultiModalInput";
import { ArrowUpRight, ShieldCheck, Lightning } from "@phosphor-icons/react";

/**
 * SeoLanding — reusable SEO-optimized landing page.
 * Sets document.title, meta description, and injects JSON-LD structured data.
 * Uses the same billboard design language as the home page for consistency.
 *
 * Props:
 *   title, description, h1, kicker, keywords, benefits, features, faqs,
 *   deepLink (path to the real tool), deepCta (button label),
 *   ctaTone: 'lime' | 'cobalt' | 'sunny' | 'crimson' | 'violet' | 'teal'
 *   includeCalculator: bool — inline the multimodal input
 */
export default function SeoLanding({
  title,
  description,
  h1,
  kicker,
  keywords = [],
  intro,
  benefits = [],
  features = [],
  faqs = [],
  deepLink = "/",
  deepCta = "Try the tool",
  ctaTone = "lime",
  toolName,
  includeCalculator = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    // ensure a meta description exists (create/update)
    const upsertMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const desc = upsertMeta("description", description);
    const ogTitle = upsertMeta("og:title", title, "property");
    const ogDesc = upsertMeta("og:description", description, "property");
    const ogType = upsertMeta("og:type", "website", "property");
    const kw = keywords.length ? upsertMeta("keywords", keywords.join(", ")) : null;
    const canonical = document.querySelector('link[rel="canonical"]') || (() => {
      const l = document.createElement("link");
      l.rel = "canonical";
      document.head.appendChild(l);
      return l;
    })();
    canonical.setAttribute("href", window.location.origin + window.location.pathname);

    // JSON-LD: SoftwareApplication + FAQPage
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-seo", "landing-jsonld");
    const payload = [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: toolName || h1,
        applicationCategory: "HealthApplication",
        operatingSystem: "Web",
        description,
        offers: { "@type": "Offer", price: 0, priceCurrency: "USD" },
      },
      faqs.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      } : null,
    ].filter(Boolean);
    ld.textContent = JSON.stringify(payload);
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      ld.remove();
      if (kw) kw.remove();
      desc.remove();
      ogTitle.remove();
      ogDesc.remove();
      ogType.remove();
    };
  }, [title, description, keywords, faqs, h1, toolName]);

  const primaryCTA = {
    lime:    "bg-[#C8FF3F] text-black hover:bg-white",
    cobalt:  "bg-[#1E40AF] text-white hover:bg-white hover:text-black",
    sunny:   "bg-[#FFD84D] text-black hover:bg-white",
    crimson: "bg-[#DC2626] text-white hover:bg-white hover:text-black",
    violet:  "bg-[#6D28D9] text-white hover:bg-white hover:text-black",
    teal:    "bg-[#0F766E] text-white hover:bg-white hover:text-black",
  }[ctaTone] || "bg-[#C8FF3F] text-black";

  return (
    <div className="relative bg-white text-black" data-testid="seo-landing">

      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10 px-6 lg:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1400px]">
          <div className="inline-flex items-center gap-2 border border-[#C8FF3F]/50 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C8FF3F]">
            <Lightning size={12} weight="fill" /> {kicker}
          </div>
          <h1
            className="mt-6 font-display font-black leading-[0.9] tracking-tighter"
            style={{ fontSize: "clamp(3rem, 9vw, 9rem)" }}
            data-testid="seo-h1"
          >
            {h1}
          </h1>
          {intro && (
            <p className="mt-8 max-w-3xl text-xl md:text-2xl leading-snug font-medium opacity-90">
              {intro}
            </p>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to={deepLink}
              data-testid="seo-primary-cta"
              className={`group inline-flex items-center gap-2.5 px-6 md:px-8 h-14 font-mono text-xs md:text-sm uppercase tracking-[0.18em] rounded-full font-bold transition-colors ${primaryCTA}`}
            >
              {deepCta}
              <ArrowUpRight size={16} weight="bold" className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <Link to="/"
              className="inline-flex items-center gap-2 px-6 h-14 border-2 border-white/30 text-white hover:border-[#C8FF3F] hover:text-[#C8FF3F] font-mono text-xs uppercase tracking-[0.18em] rounded-full transition-colors">
              See all tools
            </Link>
          </div>
        </div>
      </section>

      {/* Inline calculator (optional) */}
      {includeCalculator && (
        <section className="bg-white border-b-2 border-black px-6 lg:px-10 py-12 md:py-16">
          <div className="mx-auto max-w-[1400px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/60 mb-4">
              Try it now — no signup
            </div>
            <MultiModalInput />
          </div>
        </section>
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <section className="bg-[#C8FF3F] text-black border-y-2 border-black px-6 lg:px-10 py-16 md:py-24" data-testid="seo-benefits">
          <div className="mx-auto max-w-[1400px]">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">Why {toolName || "it"} works</div>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {benefits.map((b, i) => (
                <div key={i} className="border-2 border-black rounded-2xl p-5 bg-white/50">
                  <div className="font-display font-black text-3xl md:text-4xl tracking-tight leading-none">{b.h}</div>
                  <p className="mt-3 text-base md:text-lg leading-snug">{b.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Feature list */}
      {features.length > 0 && (
        <section className="bg-black text-white px-6 lg:px-10 py-16 md:py-24" data-testid="seo-features">
          <div className="mx-auto max-w-[1400px]">
            <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9]">
              What you get.
            </h2>
            <ul className="mt-10 grid md:grid-cols-2 gap-5">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-4 border border-white/10 rounded-2xl p-5">
                  <ShieldCheck size={26} weight="fill" className="text-[#C8FF3F] shrink-0 mt-1" />
                  <div>
                    <div className="font-display font-bold text-xl">{f.h}</div>
                    <p className="mt-1 text-base text-white/80 leading-snug">{f.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-white text-black border-t-2 border-black px-6 lg:px-10 py-16 md:py-24" data-testid="seo-faq">
          <div className="mx-auto max-w-[1400px] grid lg:grid-cols-[1fr_2fr] gap-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-black/60 mb-4">FAQ</div>
              <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-[0.9]">
                Answers, fast.
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group border-2 border-black rounded-2xl p-5 open:bg-neutral-50">
                  <summary className="cursor-pointer flex items-center justify-between gap-3">
                    <span className="font-display font-bold text-lg md:text-xl">{f.q}</span>
                    <span className="font-mono text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-base md:text-lg leading-snug text-black/80">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-[#0A0A0A] text-white px-6 lg:px-10 py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.85]">
            Ready?<br /><span className="text-[#C8FF3F]">Open the tool.</span>
          </h2>
          <Link
            to={deepLink}
            className={`inline-flex items-center gap-3 px-8 h-16 font-mono text-sm uppercase tracking-[0.2em] rounded-full font-bold ${primaryCTA}`}
          >
            {deepCta} <ArrowUpRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
